/**
 * useHostStats Hook
 *
 * Provides host dashboard statistics from Firestore data.
 */

import { useState, useEffect } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { useAuth } from '../../../contexts/AuthContext';

export interface HostStats {
  activeListings: number;
  pendingBookings: number;
  upcomingCheckins: number;
  monthlyEarnings: number;
  lastMonthEarnings: number;
  availablePayout: number;
  pendingPayout: number;
  totalViews: number;
  totalBookings: number;
}

function emailToHostId(email: string): string {
  return email.toLowerCase().replace(/[^a-z0-9]/g, '_');
}

export const useHostStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<HostStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.email) {
      setStats(null);
      setIsLoading(false);
      return;
    }

    async function fetchStats() {
      setIsLoading(true);
      try {
        const snapshot = await getDocs(collection(db, 'listings'));
        const hostId = emailToHostId(user!.email);

        const myListings = snapshot.docs.filter(
          (doc) => doc.data().hostId === hostId || doc.data().hostId === user!.uid
        );

        const activeListings = myListings.filter((d) => d.data().status === 'published').length;
        const totalViews = myListings.reduce((sum, d) => sum + (d.data().views || 0), 0);
        const totalBookings = myListings.reduce((sum, d) => sum + (d.data().bookingCount || 0), 0);

        setStats({
          activeListings,
          pendingBookings: 0,
          upcomingCheckins: 0,
          monthlyEarnings: 0,
          lastMonthEarnings: 0,
          availablePayout: 0,
          pendingPayout: 0,
          totalViews,
          totalBookings,
        });
      } catch (err) {
        console.error('Failed to fetch host stats:', err);
        setStats(null);
      } finally {
        setIsLoading(false);
      }
    }

    fetchStats();
  }, [user?.uid, user?.email]);

  return { stats, isLoading };
};

export default useHostStats;
